import kebabCase from 'lodash/kebabCase';
import { MouseEvent, useState, useRef } from 'react';

import { TipAlign } from 'ui/tooltip';
import {
  LeftSection,
  OptionBadge,
  OptionChild,
  OptionItem,
  OptionTooltip,
  SuffixWrapper,
  StyledOptionUsageOverflow,
} from './Options.styles';
import { BasicOptionProps, SelectValueType } from './types';

export const BasicOption = <TValue extends SelectValueType = string>({
  badgeText = '',
  children,
  id = '',
  isDisabled = false,
  isSelected = false,
  onClick = () => undefined,
  Prefix,
  Suffix,
  value = '' as unknown as TValue,
  role = 'option',
  tagName = 'li',
  style, // for virtualization purposes: absolute positioning styles are passed through here
  innerRef,
  tooltipContent,
  OverflowContent,
  ...props
}: BasicOptionProps<TValue>) => {
  const [showPopover, setShowPopover] = useState(false);
  const selectElementRef = useRef<HTMLDivElement>(null);
  const boundingY = selectElementRef.current?.getBoundingClientRect().y || 0;
  const parentHeightY = selectElementRef.current?.offsetParent?.getBoundingClientRect().y || 0;
  const parentWidth = selectElementRef.current?.offsetParent?.getBoundingClientRect().width || 0;
  const offsetToUse = boundingY - parentHeightY;

  return (
    <OptionTooltip
      $isDisabled={isDisabled}
      align={TipAlign.Start} // aligned to the start so that it wraps around the label
      isEnabled={!!tooltipContent}
      tooltipContent={tooltipContent}
    >
      <div
        ref={selectElementRef}
        onMouseEnter={() => setShowPopover(true)}
        onMouseLeave={() => setShowPopover(false)}
      >
        <OptionItem
          style={style}
          as={tagName}
          aria-selected={isSelected}
          aria-disabled={isDisabled}
          data-hastooltip={!!tooltipContent}
          data-test="basic-option"
          data-value={value}
          id={id}
          isDisabled={isDisabled}
          isSelected={isSelected}
          onClick={(event: MouseEvent) => onClick(value, event)}
          role={role}
          ref={innerRef}
          {...props}
        >
          <LeftSection>
            {Prefix && Prefix}
            <OptionChild data-test={`option-${kebabCase(id)}`}>{children}</OptionChild>
            {badgeText && <OptionBadge>{badgeText}</OptionBadge>}
          </LeftSection>
          {Suffix && (
            <SuffixWrapper data-test={`option-${kebabCase(id)}-suffix`}>{Suffix}</SuffixWrapper>
          )}
        </OptionItem>
      </div>
      {!!OverflowContent && showPopover && (
        <StyledOptionUsageOverflow left={parentWidth} top={offsetToUse}>
          {OverflowContent}
        </StyledOptionUsageOverflow>
      )}
    </OptionTooltip>
  );
};
